import clsx from 'clsx';
import { type JSX, memo } from 'react';

import {
  DATA_TABLE_SUB_ROW_DENSITY_PADDING_MAP,
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_SPACER,
} from '../../../constants.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import * as styles from '../SubRows.sty.js';

/**
 * Generates an SVG path based on the shape patterns we want to draw.
 */
function generatePathFromConfig(
  config: Array<'Line' | 'TShape' | 'LShape' | 'Empty'>,
  height: number,
  verticalAlignment: 'top' | 'center' | 'bottom',
  estimatedRowHeight: number,
  rowDensity: 'default' | 'condensed' | 'comfortable',
): string {
  const spacer = DATA_TABLE_SUB_ROW_SPACER;
  const rowDensityPadding = DATA_TABLE_SUB_ROW_DENSITY_PADDING_MAP[rowDensity];

  let pathString = '';
  for (const [index, shape] of config.entries()) {
    const oneBaseIndex = index + 1;
    const x = oneBaseIndex * spacer - spacer / 2;

    let yLShape = height / 2;
    if (height > estimatedRowHeight) {
      const expanderSpace =
        rowDensityPadding + DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE / 2;

      if (verticalAlignment === 'top') {
        yLShape = expanderSpace;
      } else if (verticalAlignment === 'bottom') {
        yLShape = height - expanderSpace;
      }
    }
    const yTShape = -height + yLShape;

    if (shape === 'Line') {
      pathString += `M ${x},0 V ${height} `;
    } else if (shape === 'LShape') {
      pathString += `M ${x},0 V ${yLShape} h 200 `;
    } else if (shape === 'TShape') {
      pathString += `M ${x},0 V ${height} m 0,${yTShape} h 25`;
    }
  }
  return pathString;
}

const InternalMemoizedSubRowExpanderPath = (props: {
  /**
   * The id of the row in which the path should be drawn.
   */
  rowId: string;
  /**
   * The width of the SVG.
   */
  width: number;
  /**
   * The shape that should be drawn as the path.
   */
  rowLineConfig: Array<'Line' | 'TShape' | 'LShape' | 'Empty'>;
  /**
   * Whether the row can be expanded further.
   */
  rowCanExpand?: boolean;
}): JSX.Element => {
  const { rowId, width, rowLineConfig, rowCanExpand } = props;

  const table = useDataTable();
  const estimatedRowHeight = table.estimateRowHeight({ index: 0 });
  const rowHeight = table.getRowHeight(rowId);

  const svgWidth = rowCanExpand
    ? width
    : width + DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE;

  const verticalAlignment = table.getBodyVerticalAlignment();

  return (
    <svg
      className={clsx(
        styles.expandIndentSvg,
        styles.expandIndentSvgSize({ rowCanExpand }),
      )}
      style={{
        width: svgWidth,
      }}
      preserveAspectRatio="none"
      overflow="visible"
      viewBox={`0 0 ${svgWidth} ${rowHeight}`}
    >
      <path
        d={generatePathFromConfig(
          rowLineConfig,
          rowHeight,
          verticalAlignment,
          estimatedRowHeight,
          table.atoms.visualVariantConfig?.get().rowDensity,
        )}
        fill="transparent"
        vectorEffect="non-scaling-stroke"
        strokeWidth={1}
      />
    </svg>
  );
};

/** @internal */
export const SubRowExpanderPath = memo(InternalMemoizedSubRowExpanderPath);
